/*
 * <nl-w2-story class="nl-w2-story__scene"> (sections/nl-w2-parallax-story.liquid, styles in nl-w2-story.css)
 *
 * Writes one custom property, --p (0..1), on the host: the share of the pinned scene that has been scrolled.
 * The value is eased towards the scroll target with requestAnimationFrame (critically damped lerp), so wheel steps
 * feel continuous. Scroll and resize listeners exist only while the scene is near the viewport (IntersectionObserver).
 * Each frame reads the host rect first and writes afterwards (no layout thrash). Sets data-ready once (CSS switches
 * the word and outro reveal on) and data-outro while the CTA is meant to be interactive.
 * Under reduced motion or with the theme motion setting off it does nothing: the CSS layout is static then.
 */
(() => {
  'use strict';

  if (customElements.get('nl-w2-story')) return;

  const mqReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  // final T7 (M3, JM-17b): telefony do 767 px mají sekci v normálním toku bez připnutí a bez animace (nl-w2-story.css)
  const mqPhone = window.matchMedia('(max-width: 767px)');
  const motionOff = () =>
    mqReduced.matches ||
    document.documentElement.classList.contains('nl-motion-off') ||
    Boolean(window.NL && window.NL.motion && window.NL.motion.reduced);

  const clamp = (value) => Math.min(1, Math.max(0, value));
  const EASE = 0.14;

  class W2Story extends HTMLElement {
    connectedCallback() {
      if (this._bound || motionOff()) return;
      if (mqPhone.matches) {
        if (!this._waitPhone && mqPhone.addEventListener) {
          this._waitPhone = () => {
            if (mqPhone.matches) return;
            mqPhone.removeEventListener('change', this._waitPhone);
            this._waitPhone = null;
            this.connectedCallback();
          };
          mqPhone.addEventListener('change', this._waitPhone);
        }
        return;
      }
      this._bound = true;
      this.active = false;
      this.frame = null;
      this.current = this.target();
      this.write(this.current);
      this.setAttribute('data-ready', '');

      this.onScroll = () => this.queue();
      this.onResize = () => this.queue();
      this.onMotionChange = () => {
        if (!mqReduced.matches) return;
        this.stop();
        this.style.removeProperty('--p');
        this.removeAttribute('data-ready');
      };
      if (mqReduced.addEventListener) mqReduced.addEventListener('change', this.onMotionChange);

      // fix-home-1 (QA v3): keyboard focus on the CTA while the outro is still hidden scrolls the pinned scene to the
      // point where the outro is fully shown and jumps the eased value there, so the focus ring is never invisible
      this.onFocusIn = () => {
        if (!this.hasAttribute('data-ready') || this.outro) return;
        const rect = this.getBoundingClientRect();
        const viewport = window.innerHeight || document.documentElement.clientHeight;
        const travel = rect.height - viewport;
        if (travel <= 1) return;
        window.scrollTo({ top: window.scrollY + rect.top + travel * 0.72, behavior: 'auto' });
        this.current = this.target();
        this.write(this.current);
      };
      this.addEventListener('focusin', this.onFocusIn);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) this.start();
            else this.stop();
          },
          { rootMargin: '50% 0px 50% 0px' }
        );
        this.observer.observe(this);
      } else {
        this.start();
      }
    }

    disconnectedCallback() {
      if (this._waitPhone && mqPhone.removeEventListener) mqPhone.removeEventListener('change', this._waitPhone);
      this._waitPhone = null;
      this.stop();
      if (this.observer) this.observer.disconnect();
      if (this.onFocusIn) this.removeEventListener('focusin', this.onFocusIn);
      if (mqReduced.removeEventListener && this.onMotionChange) mqReduced.removeEventListener('change', this.onMotionChange);
      this._bound = false;
    }

    start() {
      if (this.active) return;
      this.active = true;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.queue();
    }

    stop() {
      if (!this.active) return;
      this.active = false;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
      // Park the scene exactly at its end state so it never freezes half way
      this.current = this.target();
      this.write(this.current);
    }

    target() {
      const rect = this.getBoundingClientRect();
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const travel = rect.height - viewport;
      if (travel <= 1) return rect.top <= 0 ? 1 : 0;
      return clamp(-rect.top / travel);
    }

    queue() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => this.tick());
    }

    tick() {
      this.frame = null;
      const goal = this.target();
      const delta = goal - this.current;
      this.current = Math.abs(delta) < 0.0005 ? goal : this.current + delta * EASE;
      this.write(this.current);
      if (this.current !== goal && this.active) this.queue();
    }

    write(value) {
      this.style.setProperty('--p', value.toFixed(4));
      const outro = value > 0.5; // matches --o in nl-w2-story.css (visible from 0.42, complete at 0.67)
      if (outro !== this.outro) {
        this.outro = outro;
        this.toggleAttribute('data-outro', outro);
      }
    }
  }

  customElements.define('nl-w2-story', W2Story);
})();
